import {Box, Text} from '@mantine/core';
import {InvalidTokenError} from '@packages/utils';
import Image from 'next/image';
import {parseCookies} from 'nookies';
import {type FC, useState} from 'react';
import {MdOutlineBookmark, MdOutlineBookmarkBorder} from 'react-icons/md';
import type {ColumnListItemSchema} from '../../../../../core/entities/columns/schema';
import type {
  CompanyPageColumnPostType,
  NativeAdType,
} from '../../../../../core/entities/companyDetail/schema';
import {
  deleteColumnMyClips,
  updateColumnMyClips,
} from '../../../../../core/repositories/clipsRepository';
import {useIsSessionExpired} from '../../../../../core/usecases/auth/useIsSessionExpired';

type Props = {
  post: CompanyPageColumnPostType | NativeAdType | ColumnListItemSchema;
  isSponsored: boolean;
};

export const ColumnCard: FC<Props> = ({post, isSponsored}) => {
  const [isFavorited, setIsFavorited] = useState(post.favorited);
  const [favoriteCount, setFavoriteCount] = useState<number>(
    post.favoriteCount,
  );
  const {updateIsSessionExpired} = useIsSessionExpired();

  const deleteColumnUseCase = async () => {
    const cookies = parseCookies();
    const result = await deleteColumnMyClips({
      cookies,
      columnId: post.id,
    });
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    if (!result.ok) {
      return;
    }

    setIsFavorited(false);
    setFavoriteCount((prev) => Math.max(prev - 1, 0));
  };

  const updateColumnUseCase = async () => {
    const cookies = parseCookies();
    const result = await updateColumnMyClips({
      cookies,
      columnId: post.id,
    });
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    if (!result.ok) {
      return;
    }

    setIsFavorited(true);
    setFavoriteCount((prev) => prev + 1);
  };

  const handleClip = async () => {
    if (isFavorited) {
      await deleteColumnUseCase();
      return;
    }

    await updateColumnUseCase();
  };

  return (
    <div key={post.id} className="w-[300px] pc:w-[375px]">
      <Box
        component="a"
        href={`/archives/${post.id}`}
        className="cursor-pointer pc:hover:opacity-60 block"
        sx={{textDecoration: 'none', color: 'inherit'}}
      >
        <Image
          src={post.carouselThumbnailURL ?? ''}
          alt="thumbnail"
          width={375}
          height={150}
          className="rounded-16 flex"
        />
        <Box className="flex justify-between items-center mt-12">
          {isSponsored ? (
            <Box className="rounded-full border-[1px] border-black-3 px-8 py-2">
              <Text className="text-sm text-black-3">Sponsored</Text>
            </Box>
          ) : (
            <Box />
          )}
          <Box
            className={`flex cursor-pointer p-4 items-center justify-center hover:opacity-80 border-2 border-primary-1 rounded-full ${
              isFavorited ? 'bg-primary-1 text-white' : 'text-primary-1'
            }`}
            onClick={async (e) => {
              e.preventDefault(); // リンク遷移を防止
              e.stopPropagation();
              await handleClip();
            }}
          >
            {isFavorited ? (
              <MdOutlineBookmark size={20} />
            ) : (
              <MdOutlineBookmarkBorder size={20} />
            )}
            <Text className="text-sm mx-4">{favoriteCount}</Text>
          </Box>
        </Box>
        <Text className="mt-4 line-clamp-2 text-base font-bold">
          {post.title}
        </Text>
      </Box>
    </div>
  );
};
